import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Typography,
  Descriptions,
  Button,
  Spin,
  Table,
  Tag,
  Space,
  message,
  Divider,
  Row,
  Col,
  Statistic,
  Progress,
  Modal,
  Form,
  InputNumber,
  Input,
  Select,
  Tooltip,
  Empty,
} from 'antd';
import {
  ArrowLeftOutlined,
  CloudSyncOutlined,
  DollarOutlined,
  TrophyOutlined,
  EditOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  StarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  CustomerServiceOutlined,
  UserOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import { vendorService, productService, analysisService, clientVendorService, scorecardService, userService, API_BASE_URL } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';

const { Title, Paragraph } = Typography;

// Expandable text component for long descriptions
const ExpandableText = ({ text, maxLength = 300 }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  const shouldTruncate = text.length > maxLength;
  const displayText = expanded || !shouldTruncate
    ? text
    : text.slice(0, maxLength).trim() + '...';

  return (
    <div style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
      {displayText}
      {shouldTruncate && (
        <Button
          type="link"
          size="small"
          onClick={() => setExpanded(!expanded)}
          style={{ padding: '0 4px', height: 'auto' }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </div>
  );
};

const VendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [spendData, setSpendData] = useState(null);
  const [clientVendor, setClientVendor] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [scorecardTrend, setScorecardTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [spendLoading, setSpendLoading] = useState(true);
  const [scorecardLoading, setScorecardLoading] = useState(true);
  const [scrapingId, setScrapingId] = useState(null);
  const [scorecardModalVisible, setScorecardModalVisible] = useState(false);
  const [scorecardForm] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [savingOwner, setSavingOwner] = useState(false);

  const canEditScorecard = can('scorecards', 'edit');
  const canEditVendor = can('vendors', 'edit');

  useEffect(() => {
    fetchVendorDetails();
    fetchVendorProducts();
    fetchVendorSpend();
    fetchClientVendorAndScorecard();
    fetchUsers();
  }, [id]);

  const fetchUsers = async () => {
    try {
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      console.log('Failed to fetch users');
    }
  };

  const fetchClientVendorAndScorecard = async () => {
    try {
      setScorecardLoading(true);
      // Get the client vendor for this master vendor
      const cv = await clientVendorService.getByVendorId(id);
      setClientVendor(cv);

      if (cv) {
        // Fetch latest scorecard
        const sc = await scorecardService.getLatestForVendor(cv.id);
        setScorecard(sc);

        // Fetch trend data
        const trend = await scorecardService.getVendorTrend(cv.id, 12);
        setScorecardTrend(trend);
      }
    } catch (error) {
      // Client vendor may not exist for this org - that's ok
      console.log('No client vendor found for this vendor');
      setClientVendor(null);
      setScorecard(null);
    } finally {
      setScorecardLoading(false);
    }
  };

  const fetchVendorSpend = async () => {
    try {
      setSpendLoading(true);
      const data = await analysisService.getVendorSpendDetail(id);
      setSpendData(data);
    } catch (error) {
      console.error('Error fetching vendor spend:', error);
      // Don't show error message - spend data may not exist for all vendors
    } finally {
      setSpendLoading(false);
    }
  };

  const fetchVendorDetails = async () => {
    try {
      setLoading(true);
      const data = await vendorService.getById(id);
      setVendor(data);
    } catch (error) {
      console.error('Error fetching vendor details:', error);
      message.error('Failed to fetch vendor details');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorProducts = async () => {
    try {
      setProductsLoading(true);
      console.log(`Fetching scraped products for vendor ID: ${id}`);
      // Use the scraped products endpoint instead of the regular products endpoint
      const response = await axios.get(
        `${API_BASE_URL}/api/scraping/products/scraped/public?vendor_id=${id}&limit=200`
      );
      const data = response.data;
      console.log('Received vendor products data:', data);
      if (Array.isArray(data)) {
        setProducts(data);
        console.log('Successfully set products state.');
      } else {
        console.error('Received non-array data for products:', data);
        setProducts([]);
        message.error('Received invalid product data format');
      }
    } catch (error) {
      console.error('Caught error object in fetchVendorProducts:', error);
      if (error && error.response) {
        console.error('Caught error response data:', error.response.data);
        console.error('Caught error response status:', error.response.status);
      } else if (error) {
        console.error('Caught error message:', error.message);
      }
      message.error('Failed to fetch vendor products');
    } finally {
      setProductsLoading(false);
    }
  };

  const handleScrape = async (productId) => {
    try {
      setScrapingId(productId);
      await productService.scrapeProductData(productId);
      message.success('Product data scraped successfully');
      fetchVendorProducts();
    } catch (error) {
      console.error('Error scraping product data:', error);
      message.error('Failed to scrape product data');
    } finally {
      setScrapingId(null);
    }
  };

  const handleSync = async () => {
    try {
      await vendorService.syncVendor(id);
      message.success('Vendor data synchronized successfully');
      fetchVendorDetails();
      fetchVendorProducts();
    } catch (error) {
      console.error('Error syncing vendor data:', error);
      message.error('Failed to sync vendor data');
    }
  };

  const handleBusinessOwnerChange = async (newOwnerId) => {
    if (!clientVendor) return;
    try {
      setSavingOwner(true);
      await clientVendorService.update(clientVendor.id, { business_owner_id: newOwnerId || null });
      message.success('Business owner updated successfully');
      fetchClientVendorAndScorecard();
    } catch (error) {
      console.error('Error updating business owner:', error);
      message.error(error.response?.data?.detail || 'Failed to update business owner');
    } finally {
      setSavingOwner(false);
    }
  };

  const handleContractOwnerChange = async (newOwnerId) => {
    if (!clientVendor) return;
    try {
      setSavingOwner(true);
      await clientVendorService.update(clientVendor.id, { contract_owner_id: newOwnerId || null });
      message.success('Contract owner updated successfully');
      fetchClientVendorAndScorecard();
    } catch (error) {
      console.error('Error updating contract owner:', error);
      message.error(error.response?.data?.detail || 'Failed to update contract owner');
    } finally {
      setSavingOwner(false);
    }
  };

  const openScorecardModal = () => {
    const now = new Date();
    if (scorecard) {
      // Edit existing
      scorecardForm.setFieldsValue({
        quality_score: scorecard.quality_score,
        delivery_score: scorecard.delivery_score,
        cost_score: scorecard.cost_score,
        responsiveness_score: scorecard.responsiveness_score,
        notes: scorecard.notes,
        year: scorecard.year,
        month: scorecard.month,
      });
    } else {
      // Create new
      scorecardForm.setFieldsValue({
        quality_score: 75,
        delivery_score: 75,
        cost_score: 75,
        responsiveness_score: 75,
        notes: '',
        year: now.getFullYear(),
        month: now.getMonth() + 1,
      });
    }
    setScorecardModalVisible(true);
  };

  const handleScorecardSave = async (values) => {
    try {
      if (scorecard) {
        // Update existing
        await scorecardService.update(scorecard.id, values);
        message.success('Scorecard updated successfully');
      } else {
        // Create new
        await scorecardService.create({
          ...values,
          client_vendor_id: clientVendor.id,
        });
        message.success('Scorecard created successfully');
      }
      setScorecardModalVisible(false);
      fetchClientVendorAndScorecard();
    } catch (error) {
      console.error('Error saving scorecard:', error);
      message.error(error.response?.data?.detail || 'Failed to save scorecard');
    }
  };

  // Score color helper
  const getScoreColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    if (score >= 40) return '#fa8c16';
    return '#f5222d';
  };

  // Scorecard trend chart options
  const getScorecardTrendOption = () => {
    if (!scorecardTrend.length) return {};

    return {
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: ['Overall', 'Quality', 'Delivery', 'Cost', 'Responsiveness'],
        bottom: 0,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: scorecardTrend.map(t => `${t.year}-${String(t.month).padStart(2, '0')}`),
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
      },
      series: [
        {
          name: 'Overall',
          type: 'line',
          data: scorecardTrend.map(t => t.overall_score),
          lineStyle: { width: 3 },
          itemStyle: { color: '#1890ff' },
        },
        {
          name: 'Quality',
          type: 'line',
          data: scorecardTrend.map(t => t.quality_score),
          itemStyle: { color: '#52c41a' },
        },
        {
          name: 'Delivery',
          type: 'line',
          data: scorecardTrend.map(t => t.delivery_score),
          itemStyle: { color: '#722ed1' },
        },
        {
          name: 'Cost',
          type: 'line',
          data: scorecardTrend.map(t => t.cost_score),
          itemStyle: { color: '#faad14' },
        },
        {
          name: 'Responsiveness',
          type: 'line',
          data: scorecardTrend.map(t => t.responsiveness_score),
          itemStyle: { color: '#eb2f96' },
        },
      ],
    };
  };

  const productColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 400,
      render: (text) => (
        <Paragraph
          ellipsis={{
            rows: 2,
            expandable: 'collapsible',
            symbol: (expanded) => expanded ? 'Show less' : 'Show more'
          }}
          style={{ marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {text || 'No description available'}
        </Paragraph>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (text) => text ? <Tag color="blue">{text}</Tag> : 'N/A',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (text) => text ? `$${text.toLocaleString()}` : 'N/A',
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<CloudSyncOutlined />}
            size="small"
            onClick={() => handleScrape(record.id)}
            loading={scrapingId === record.id}
          >
            Scrape Data
          </Button>
        </Space>
      ),
    },
  ];

  // Spend chart options
  const getSpendChartOption = () => {
    if (!spendData?.monthly_spend?.length) return {};

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          let result = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach(p => {
            result += `${p.marker} ${p.seriesName}: $${p.value.toLocaleString()}<br/>`;
          });
          return result;
        }
      },
      legend: {
        data: ['Actual Spend', 'Budget'],
        bottom: 0
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: spendData.monthly_spend.map(m => m.month),
        axisLabel: { rotate: 45 }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value) => `$${(value / 1000).toFixed(0)}k`
        }
      },
      series: [
        {
          name: 'Actual Spend',
          type: 'bar',
          data: spendData.monthly_spend.map(m => m.spend),
          itemStyle: { color: '#5470c6' }
        },
        {
          name: 'Budget',
          type: 'line',
          data: spendData.monthly_spend.map(m => m.budget),
          smooth: true,
          lineStyle: { type: 'dashed' },
          itemStyle: { color: '#91cc75' }
        }
      ]
    };
  };

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        Back
      </Button>
      
      <Spin spinning={loading}>
        {vendor && (
          <>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>{vendor.name}</Title>
                <Button onClick={handleSync}>Sync Vendor Data</Button>
              </div>
              
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Website">
                  {vendor.website ? (
                    <a href={vendor.website} target="_blank" rel="noopener noreferrer">
                      {vendor.website}
                    </a>
                  ) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Category">
                  {spendData?.vendor?.category ? (
                    <Tag color="blue">{spendData.vendor.category}</Tag>
                  ) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Contact Email">
                  {vendor.contact_email || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Contact Phone">
                  {vendor.contact_phone || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label={<span><UserOutlined style={{ marginRight: 4 }} />Business Owner</span>}>
                  {clientVendor ? (
                    canEditVendor ? (
                      <Select
                        style={{ width: 200 }}
                        value={clientVendor.business_owner_id}
                        onChange={handleBusinessOwnerChange}
                        loading={savingOwner}
                        allowClear
                        placeholder="Select business owner"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {users.map(user => (
                          <Select.Option key={user.id} value={user.id}>
                            {user.full_name || user.email}
                          </Select.Option>
                        ))}
                      </Select>
                    ) : (
                      <span>
                        {clientVendor.business_owner
                          ? clientVendor.business_owner.full_name || clientVendor.business_owner.email
                          : 'No business owner assigned'}
                      </span>
                    )
                  ) : (
                    <span style={{ color: '#999' }}>Not in your vendor list</span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={<span><UserOutlined style={{ marginRight: 4 }} />Contract Owner</span>}>
                  {clientVendor ? (
                    canEditVendor ? (
                      <Select
                        style={{ width: 200 }}
                        value={clientVendor.contract_owner_id}
                        onChange={handleContractOwnerChange}
                        loading={savingOwner}
                        allowClear
                        placeholder="Select contract owner"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {users.map(user => (
                          <Select.Option key={user.id} value={user.id}>
                            {user.full_name || user.email}
                          </Select.Option>
                        ))}
                      </Select>
                    ) : (
                      <span>
                        {clientVendor.contract_owner
                          ? clientVendor.contract_owner.full_name || clientVendor.contract_owner.email
                          : 'No contract owner assigned'}
                      </span>
                    )
                  ) : (
                    <span style={{ color: '#999' }}>Not in your vendor list</span>
                  )}
                </Descriptions.Item>
                {vendor.description && (
                  <Descriptions.Item label="Description" span={2}>
                    <ExpandableText text={vendor.description} maxLength={300} />
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Vendor Scorecard Section */}
            <Card style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>
                  <TrophyOutlined style={{ marginRight: 8 }} />
                  Vendor Scorecard
                </Title>
                <Space>
                  {scorecard && (
                    <Link to="/vendor-scorecards">
                      <Button type="link">View All Scorecards</Button>
                    </Link>
                  )}
                  {canEditScorecard && clientVendor && (
                    <Button
                      type="primary"
                      icon={scorecard ? <EditOutlined /> : <PlusOutlined />}
                      onClick={openScorecardModal}
                    >
                      {scorecard ? 'Edit Score' : 'Add Score'}
                    </Button>
                  )}
                </Space>
              </div>

              <Spin spinning={scorecardLoading}>
                {!clientVendor ? (
                  <Empty
                    description="This vendor is not in your organization's vendor list"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : scorecard ? (
                  <>
                    {/* Overall Score Display */}
                    <Row gutter={[24, 16]}>
                      <Col xs={24} md={8}>
                        <Card size="small" style={{ textAlign: 'center' }}>
                          <Progress
                            type="circle"
                            percent={scorecard.overall_score}
                            strokeColor={getScoreColor(scorecard.overall_score)}
                            format={(percent) => (
                              <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                {percent}
                              </span>
                            )}
                          />
                          <div style={{ marginTop: 12, fontWeight: 600 }}>Overall Score</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {scorecard.year}-{String(scorecard.month).padStart(2, '0')}
                          </div>
                        </Card>
                      </Col>

                      <Col xs={24} md={16}>
                        <Row gutter={[16, 16]}>
                          <Col span={12}>
                            <Card size="small">
                              <Statistic
                                title={
                                  <span>
                                    <CheckCircleOutlined style={{ marginRight: 4 }} />
                                    Quality
                                  </span>
                                }
                                value={scorecard.quality_score}
                                suffix="/ 100"
                                valueStyle={{ color: getScoreColor(scorecard.quality_score) }}
                              />
                              <Progress
                                percent={scorecard.quality_score}
                                showInfo={false}
                                strokeColor={getScoreColor(scorecard.quality_score)}
                                size="small"
                              />
                            </Card>
                          </Col>
                          <Col span={12}>
                            <Card size="small">
                              <Statistic
                                title={
                                  <span>
                                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                                    Delivery
                                  </span>
                                }
                                value={scorecard.delivery_score}
                                suffix="/ 100"
                                valueStyle={{ color: getScoreColor(scorecard.delivery_score) }}
                              />
                              <Progress
                                percent={scorecard.delivery_score}
                                showInfo={false}
                                strokeColor={getScoreColor(scorecard.delivery_score)}
                                size="small"
                              />
                            </Card>
                          </Col>
                          <Col span={12}>
                            <Card size="small">
                              <Statistic
                                title={
                                  <span>
                                    <DollarOutlined style={{ marginRight: 4 }} />
                                    Cost
                                  </span>
                                }
                                value={scorecard.cost_score}
                                suffix="/ 100"
                                valueStyle={{ color: getScoreColor(scorecard.cost_score) }}
                              />
                              <Progress
                                percent={scorecard.cost_score}
                                showInfo={false}
                                strokeColor={getScoreColor(scorecard.cost_score)}
                                size="small"
                              />
                            </Card>
                          </Col>
                          <Col span={12}>
                            <Card size="small">
                              <Statistic
                                title={
                                  <span>
                                    <CustomerServiceOutlined style={{ marginRight: 4 }} />
                                    Responsiveness
                                  </span>
                                }
                                value={scorecard.responsiveness_score}
                                suffix="/ 100"
                                valueStyle={{ color: getScoreColor(scorecard.responsiveness_score) }}
                              />
                              <Progress
                                percent={scorecard.responsiveness_score}
                                showInfo={false}
                                strokeColor={getScoreColor(scorecard.responsiveness_score)}
                                size="small"
                              />
                            </Card>
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    {/* Risk Score */}
                    {scorecard.risk_score > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <Card size="small">
                          <Row align="middle" gutter={16}>
                            <Col>
                              <SafetyCertificateOutlined style={{ fontSize: 24, color: getScoreColor(100 - scorecard.risk_score) }} />
                            </Col>
                            <Col flex="auto">
                              <div style={{ fontWeight: 500 }}>Risk Score: {scorecard.risk_score}/100</div>
                              <Progress
                                percent={scorecard.risk_score}
                                showInfo={false}
                                strokeColor={getScoreColor(100 - scorecard.risk_score)}
                                size="small"
                              />
                            </Col>
                            {scorecard.risk_factors && Object.keys(scorecard.risk_factors).length > 0 && (
                              <Col>
                                <Tooltip title={Object.keys(scorecard.risk_factors).map(k => k.replace(/_/g, ' ')).join(', ')}>
                                  <InfoCircleOutlined style={{ color: '#faad14' }} />
                                </Tooltip>
                              </Col>
                            )}
                          </Row>
                        </Card>
                      </div>
                    )}

                    {/* Trend Chart */}
                    {scorecardTrend.length > 1 && (
                      <div style={{ marginTop: 24 }}>
                        <Title level={5}>Score Trend</Title>
                        <ReactECharts option={getScorecardTrendOption()} style={{ height: 250 }} />
                      </div>
                    )}

                    {/* Notes */}
                    {scorecard.notes && (
                      <div style={{ marginTop: 16 }}>
                        <Title level={5}>Notes</Title>
                        <p style={{ color: '#666' }}>{scorecard.notes}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <Empty
                    description={
                      <span>
                        No scorecard for this vendor yet.
                        {canEditScorecard && ' Click "Add Score" to create one.'}
                      </span>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Spin>
            </Card>

            {/* Spend Analytics Section */}
            <Card style={{ marginTop: 24 }}>
              <Title level={3}>
                <DollarOutlined style={{ marginRight: 8 }} />
                Spend Analytics
              </Title>
              <Spin spinning={spendLoading}>
                {spendData?.monthly_spend?.length > 0 ? (
                  <>
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                      <Col xs={24} sm={8}>
                        <Card size="small">
                          <Statistic
                            title="Total Spend (12 months)"
                            value={spendData?.summary?.total_spend || 0}
                            precision={0}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                            formatter={(value) => `$${value.toLocaleString()}`}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small">
                          <Statistic
                            title="Monthly Average"
                            value={spendData?.summary?.avg_monthly || 0}
                            precision={0}
                            prefix={<DollarOutlined />}
                            formatter={(value) => `$${value.toLocaleString()}`}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small">
                          <Statistic
                            title="Total Budget"
                            value={spendData?.summary?.total_budget || 0}
                            precision={0}
                            prefix={<DollarOutlined />}
                            formatter={(value) => `$${value.toLocaleString()}`}
                          />
                        </Card>
                      </Col>
                    </Row>
                    <ReactECharts
                      option={getSpendChartOption()}
                      style={{ height: 300 }}
                    />
                  </>
                ) : (
                  <p>No spend data available for this vendor.</p>
                )}
              </Spin>
            </Card>

            <Divider />
            
            <Card>
              <Title level={3}>Products</Title>
              <Spin spinning={productsLoading}>
                <Table
                  columns={productColumns}
                  dataSource={products}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                />
              </Spin>
            </Card>
          </>
        )}
      </Spin>

      {/* Scorecard Modal */}
      <Modal
        title={scorecard ? 'Edit Vendor Scorecard' : 'Add Vendor Scorecard'}
        open={scorecardModalVisible}
        onCancel={() => setScorecardModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={scorecardForm}
          layout="vertical"
          onFinish={handleScorecardSave}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="year"
                label="Year"
                rules={[{ required: true, message: 'Please select year' }]}
              >
                <Select disabled={!!scorecard}>
                  {[...Array(5)].map((_, i) => {
                    const year = new Date().getFullYear() - i;
                    return <Select.Option key={year} value={year}>{year}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="month"
                label="Month"
                rules={[{ required: true, message: 'Please select month' }]}
              >
                <Select disabled={!!scorecard}>
                  {[...Array(12)].map((_, i) => (
                    <Select.Option key={i + 1} value={i + 1}>
                      {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider>Performance Scores (0-100)</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quality_score"
                label={
                  <span>
                    <CheckCircleOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                    Quality Score
                  </span>
                }
                rules={[{ required: true, message: 'Please enter quality score' }]}
                tooltip="Product/service quality, defect rates, accuracy"
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="delivery_score"
                label={
                  <span>
                    <ClockCircleOutlined style={{ marginRight: 4, color: '#722ed1' }} />
                    Delivery Score
                  </span>
                }
                rules={[{ required: true, message: 'Please enter delivery score' }]}
                tooltip="On-time delivery, lead times, reliability"
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cost_score"
                label={
                  <span>
                    <DollarOutlined style={{ marginRight: 4, color: '#faad14' }} />
                    Cost Score
                  </span>
                }
                rules={[{ required: true, message: 'Please enter cost score' }]}
                tooltip="Value for money, pricing competitiveness, cost stability"
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="responsiveness_score"
                label={
                  <span>
                    <CustomerServiceOutlined style={{ marginRight: 4, color: '#eb2f96' }} />
                    Responsiveness Score
                  </span>
                }
                rules={[{ required: true, message: 'Please enter responsiveness score' }]}
                tooltip="Communication, issue resolution, support quality"
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Notes"
            tooltip="Additional comments about this scoring period"
          >
            <Input.TextArea rows={3} placeholder="Optional notes about this assessment..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setScorecardModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {scorecard ? 'Update Scorecard' : 'Create Scorecard'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VendorDetail;